import { DateField } from "./DateField";
import { NumberField } from "./NumberField";

interface NumberRangeProps {
  kind: "number";
  /** `NaN` while a box is empty (what `NumberField` reports for a cleared box). */
  value: { from: number; to: number };
  onChange: (value: { from: number; to: number }) => void;
  /** Whole numbers only (ages, a count of months). */
  minValue?: number;
}

interface DateRangeProps {
  kind: "date";
  /** ISO dates (`2026-01-01`), `""` while not entered. */
  value: { from: string; to: string };
  onChange: (value: { from: string; to: string }) => void;
  minValue?: never;
}

type RangeFieldsProps = (NumberRangeProps | DateRangeProps) & {
  fromLabel: string;
  toLabel: string;
  onBlur?: () => void;
  isDisabled?: boolean;
  /** Shown under the first box. */
  fromError?: string;
  /** Shown under the second box (« La fin doit suivre le début. »). */
  toError?: string;
};

/**
 * Two boxes for a range, side by side: from and to, numbers or dates. Used by the age, date and
 * number questions of the library. It only draws and reports; whether the end is greater than the
 * start is the form's rule (and the API's), shown through `toError`.
 */
export function RangeFields(props: RangeFieldsProps) {
  const { fromLabel, toLabel, onBlur, isDisabled, fromError, toError } = props;

  return (
    <div className="grid grid-cols-2 items-start gap-md">
      {props.kind === "number" ? (
        <>
          <NumberField
            label={fromLabel}
            minValue={props.minValue}
            value={props.value.from}
            onChange={(from) => {
              props.onChange({ ...props.value, from });
            }}
            onBlur={onBlur}
            isDisabled={isDisabled}
            errorMessage={fromError}
          />
          <NumberField
            label={toLabel}
            minValue={props.minValue}
            value={props.value.to}
            onChange={(to) => {
              props.onChange({ ...props.value, to });
            }}
            onBlur={onBlur}
            isDisabled={isDisabled}
            errorMessage={toError}
          />
        </>
      ) : (
        <>
          <DateField
            label={fromLabel}
            value={props.value.from}
            onChange={(from) => {
              props.onChange({ ...props.value, from });
            }}
            onBlur={onBlur}
            isDisabled={isDisabled}
            errorMessage={fromError}
          />
          <DateField
            label={toLabel}
            value={props.value.to}
            onChange={(to) => {
              props.onChange({ ...props.value, to });
            }}
            onBlur={onBlur}
            isDisabled={isDisabled}
            errorMessage={toError}
          />
        </>
      )}
    </div>
  );
}

export type { RangeFieldsProps };
